import { useStore } from "../store";
import { kidStyle } from "../kid-style";
import { todayLine } from "../format";
import { ScoreNumber } from "../components/ScoreNumber";
import { Emoji } from "../components/Emoji";
import { WeekStrip } from "../components/WeekStrip";
import { InstallBanner } from "../components/InstallBanner";
import { PointzLogo } from "../components/PointzLogo";
import { KidAvatar } from "../components/KidAvatar";

/**
 * One big card per kid on their colour: photo, name, score, today's movement, and stars, streak
 * and goal (read only; grown-ups change it in Settings). Tap to open. Under them, the week so far
 * for every kid.
 */
export function Home({
  onOpenSettings,
  onOpenKid,
  onInstallGuide,
}: {
  onOpenSettings: () => void;
  onOpenKid: (kidId: string) => void;
  onInstallGuide: () => void;
}) {
  const { snapshot } = useStore();
  if (!snapshot) return null;
  const isAdult = snapshot.me.role === "adult";

  return (
    <main className="screen">
      <h1 className="brand brand--home">
        <PointzLogo />
      </h1>
      <InstallBanner onOpen={onInstallGuide} />
      {snapshot.kids.length === 0 ? (
        <section className="empty">
          <p className="empty__title">No kids yet</p>
          {isAdult ? (
            <>
              <p className="hint">Add each kid in Settings, then give them their sign-in code.</p>
              <button className="button button--primary" type="button" onClick={onOpenSettings}>
                Go to Settings
              </button>
            </>
          ) : (
            <p className="hint">Ask a grown-up to add you.</p>
          )}
        </section>
      ) : (
        <ul className="kid-cards">
          {snapshot.kids.map((kid) => (
            <li key={kid.id}>
              <button
                type="button"
                className="kid-card kid-card--button"
                style={kidStyle(kid.colour)}
                onClick={() => onOpenKid(kid.id)}
              >
                <span className="kid-card__top">
                  <span className="kid-card__who">
                    <KidAvatar kid={kid} size="lg" />
                    <span className="kid-card__name">{kid.name}</span>
                  </span>
                  {snapshot.me.role === "child" && snapshot.me.kidId === kid.id && <span className="kid-card__tag">You</span>}
                </span>
                <ScoreNumber value={kid.score} className="kid-card__score" />
                <span className="kid-card__today">{todayLine(kid.todayNet)}</span>
                <span className="kid-card__stars">
                  <span className="kid-card__stat">
                    <Emoji>⭐</Emoji>
                    <span className="visually-hidden">Stars</span> {kid.stars}
                  </span>
                  ·
                  <span className="kid-card__stat">
                    <Emoji>🔥</Emoji>
                    <span className="visually-hidden">streak</span> {kid.streak}
                  </span>
                  ·
                  <span className="kid-card__stat">
                    <Emoji>🎯</Emoji>
                    <span className="visually-hidden">goal</span> {kid.goal}
                  </span>
                  {kid.today.star && (
                    <span className="kid-card__today-star">
                      · <Emoji>⭐</Emoji>
                      <span className="visually-hidden">earned</span> today
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {snapshot.kids.length > 0 && <WeekStrip kids={snapshot.kids} />}
    </main>
  );
}
